/**
 * Map index API + Redux scriptIndexList rows to fixed strip slots (SENSEX / NIFTY 50 / BANK NIFTY).
 * Do not rely on Map.values() order — keys may be id:* and server order can change.
 */

function normBlob(row) {
  return [row?.displayName, row?.script, row?.symbol, row?.name]
    .filter(Boolean)
    .join(" ")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeIndexAliasText(value) {
  return String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/**
 * Canonical benchmark index alias from raw symbol/token text.
 * Keeps matching strict to avoid routing derivative/futures ticks into benchmark slots.
 */
export function canonicalBenchmarkIndexAlias(value) {
  const s = normalizeIndexAliasText(value);
  if (!s) return "";
  if (s === "SENSEX" || s === "BSESENSEX") return "SENSEX";
  if (
    s === "NIFTY" ||
    s === "NIFTY50" ||
    s === "CNXNIFTY" ||
    s === "CNXNIFTY50"
  ) return "NIFTY";
  if (
    s === "BANKNIFTY" ||
    s === "NIFTYBANK" ||
    s === "BNIFTY" ||
    s === "CNXBANKNIFTY"
  ) return "BANKNIFTY";
  return "";
}

/** Canonical benchmark alias for an index row (from its known identity fields). */
export function rowBenchmarkIndexAlias(row) {
  const ids = indexRowIdentityTokens(row);
  for (const id of ids) {
    const a = canonicalBenchmarkIndexAlias(id);
    if (a) return a;
  }
  return "";
}

function benchmarkAliasSubscriptionTokens(canonicalAlias) {
  if (canonicalAlias === "SENSEX") {
    return ["SENSEX", "BSESENSEX", "BSE SENSEX"];
  }
  if (canonicalAlias === "NIFTY") {
    return ["NIFTY", "NIFTY50", "NIFTY 50", "CNXNIFTY", "CNX NIFTY"];
  }
  if (canonicalAlias === "BANKNIFTY") {
    return [
      "BANKNIFTY",
      "BANK NIFTY",
      "NIFTYBANK",
      "NIFTY BANK",
      "BNIFTY",
      "B NIFTY",
      "CNXBANKNIFTY",
      "CNX BANKNIFTY",
    ];
  }
  return [];
}

/** Futures/options style label — not a cash index; exclude from header benchmark slots. */
export function rowLooksLikeFoOrExpiry(row) {
  const b = normBlob(row);
  if (!b) return false;
  if (/\b(CE|PE)\b\d|\b\d{2,5}\s*(CE|PE)\b/i.test(b)) return true;
  if (/\b(FUT|FUTURE)\b/i.test(b)) return true;
  if (/\b\d{1,2}\s*[A-Z]{3}\s*\d{4}\b/.test(b)) return true;
  if (/\d{2}[A-Za-z]{3}\d{4}/.test(b.replace(/\s+/g, ""))) return true;
  return false;
}

/**
 * Choose previous close for change%: ignore absurd tick fields (volume mis-mapped as close, etc.).
 */
export function pickReasonablePrevClose(ltp, tickPrevClose, row) {
  const l = Number(ltp);
  const t = Number(tickPrevClose) || 0;
  const rowPc = Number(
    row?.previousClose ??
      row?.PreviousClose ??
      row?.prevClose ??
      row?.close ??
      row?.Close ??
      0
  );
  if (!Number.isFinite(l) || l <= 0) {
    if (Number.isFinite(t) && t > 0) return t;
    if (Number.isFinite(rowPc) && rowPc > 0) return rowPc;
    return 0;
  }
  const plausible = (p) => {
    if (!Number.isFinite(p) || p <= 0) return false;
    const ratio = p / l;
    return ratio >= 0.35 && ratio <= 4;
  };
  // Prefer row snapshot close first: socket ticks occasionally carry mismapped PrevClose.
  // Using row close as primary keeps index % stable instead of jumping between 0% and extreme values.
  if (plausible(rowPc)) return rowPc;
  if (plausible(t)) return t;
  const open = Number(row?.open ?? row?.Open ?? 0);
  if (plausible(open)) return open;
  /** Do not fall back to raw t/rowPc — ticks often mis-map volume/OI into PrevClose (e.g. SENSEX −99%). */
  return l;
}

function takeNextUnused(rows, used) {
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    if (used.has(r)) continue;
    used.add(r);
    return r;
  }
  return null;
}

/**
 * @param {Map|unknown} scriptIndexList
 * @returns {{ sensex: object, nifty: object, bankNifty: object }}
 */
export function pickIndexStripRows(scriptIndexList) {
  const rows =
    scriptIndexList instanceof Map
      ? [...scriptIndexList.values()]
      : Array.isArray(scriptIndexList)
        ? scriptIndexList
        : [];

  const spotRows = rows.filter((row) => row && typeof row === "object" && !rowLooksLikeFoOrExpiry(row));

  let sensex = null;
  let nifty = null;
  let bankNifty = null;

  const classify = (pool) => {
    for (const row of pool) {
      if (!row || typeof row !== "object") continue;
      const b = normBlob(row);
      if (!b) continue;

      const isNifty50Spot = /^(CNX\s*)?NIFTY\s*50$|^NIFTY50$/i.test(
        b.replace(/\s+/g, " ").trim()
      );
      if (
        !bankNifty &&
        !isNifty50Spot &&
        /BANKNIFTY|BANK\s*NIFTY|NIFTY\s*BANK|CNX\s*BANKNIFTY|\bBNIFTY\b|^B\s*NIFTY/i.test(
          b
        )
      ) {
        bankNifty = row;
        continue;
      }
      if (!sensex && /\bSENSEX\b|BSE\s*SENSEX/i.test(b)) {
        sensex = row;
        continue;
      }
      if (
        !nifty &&
        /\bNIFTY\b/.test(b) &&
        !/BANK/i.test(b) &&
        !/BANKNIFTY/i.test(b) &&
        !/\bSENSEX\b/i.test(b)
      ) {
        nifty = row;
        continue;
      }
      if (
        !nifty &&
        /^(CNX\s*)?NIFTY\s*50|^NIFTY50$/i.test(b.replace(/\s+/g, " "))
      ) {
        nifty = row;
        continue;
      }
    }
  };

  classify(spotRows.length > 0 ? spotRows : rows);

  const used = new Set();
  if (sensex) used.add(sensex);
  if (nifty) used.add(nifty);
  if (bankNifty) used.add(bankNifty);

  const poolForFallback = spotRows.length > 0 ? spotRows : rows;
  const empty = {};

  return {
    sensex: sensex ?? takeNextUnused(poolForFallback, used) ?? empty,
    nifty: nifty ?? takeNextUnused(poolForFallback, used) ?? empty,
    bankNifty: bankNifty ?? takeNextUnused(poolForFallback, used) ?? empty,
  };
}

/** LTP from row — API/socket field names vary */
/** Join index room + subscribe (batch + per-symbol, like Chart) so feeds that expect either still deliver ticks. */
export function emitIndexSocketSubscriptions(sock, scriptIndexList) {
  if (!sock || !(scriptIndexList instanceof Map) || scriptIndexList.size === 0) return;
  const tokens = indexRowsSubscriptionTokens(scriptIndexList);
  if (!tokens.length) return;
  const roomStr = tokens.toString();
  sock.emit("room", roomStr);
  sock.emit("subscribe", { symbols: tokens });
  for (let i = 0; i < tokens.length; i += 1) {
    sock.emit("subscribe", { symbol: tokens[i] });
  }
}

/** All strings / tokens the socket may use for this index row (subscribe + tick matching). */
export function indexRowIdentityTokens(row) {
  if (!row || typeof row !== "object") return [];
  const out = [];
  const seen = new Set();
  const add = (x) => {
    if (x == null || x === "") return;
    const s = String(x).trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  const keys = [
    "displayName",
    "script",
    "symbol",
    "name",
    "label",
    "Script",
    "Symbol",
    "instrumentToken",
    "instrument_token",
    "InstrumentToken",
    "token",
    "Token",
    "securityId",
    "SecurityId",
    "scriptCode",
    "bcastToken",
    "socketSymbol",
    "nseSymbol",
    "bseSymbol",
    "isin",
    "ISIN",
  ];
  for (const k of keys) add(row[k]);
  if (row.id != null && row.id !== "") add(String(row.id));
  return out;
}

/** One row: all socket join keys (names + tokens). Fallback to display symbol when row missing. */
export function singleRowSocketSubscriptionTokens(row, displaySymbol) {
  const sym = String(displaySymbol ?? "").trim();
  if (!row || typeof row !== "object") {
    return sym ? [sym] : [];
  }
  const ids = indexRowIdentityTokens(row);
  if (!sym) return ids;
  if (ids.some((x) => String(x).trim() === sym)) return ids;
  return [sym, ...ids];
}

/** Same pattern as `emitIndexSocketSubscriptions` but for the chart’s single symbol row. Returns `roomStr` for `leave_room`. */
export function emitSingleRowSocketSubscriptions(sock, row, displaySymbol) {
  const tokens = singleRowSocketSubscriptionTokens(row, displaySymbol);
  if (!tokens.length || !sock) return "";
  const roomStr = tokens.join(",");
  sock.emit("room", roomStr);
  sock.emit("subscribe", { symbols: tokens });
  for (let i = 0; i < tokens.length; i += 1) {
    sock.emit("subscribe", { symbol: tokens[i] });
  }
  return roomStr;
}

/** Unique strings to join index socket room + subscribe (names + instrument tokens). */
export function indexRowsSubscriptionTokens(scriptIndexList) {
  const rows =
    scriptIndexList instanceof Map
      ? [...scriptIndexList.values()]
      : Array.isArray(scriptIndexList)
        ? scriptIndexList
        : [];

  const out = [];
  const seen = new Set();
  const pushAll = (arr) => {
    for (const s of arr) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
  };

  for (const v of rows) {
    if (!v || typeof v !== "object") continue;
    pushAll(indexRowIdentityTokens(v));
    const alias = rowBenchmarkIndexAlias(v);
    if (alias) pushAll(benchmarkAliasSubscriptionTokens(alias));
  }
  return out;
}

export function indexRowLtp(row) {
  if (!row || typeof row !== "object") return 0;
  const v =
    row.lastTradedPrice ??
    row.ltp ??
    row.LTP ??
    row.LastTradedPrice ??
    row.close ??
    row.Close;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Point change and % for index / watchlist rows — same sanity rules as socket `chgFmt` (reasonable prev only).
 * Prefer this over raw `changeAmount` / `changePercent` in the top strip when API fields are wrong.
 */
export function indexRowDisplayChange(row) {
  if (!row || typeof row !== "object") return { delta: 0, pct: 0 };
  const ltp = indexRowLtp(row);
  const tickPc = Number(
    row.PrevClose ??
      row.prevClose ??
      row.prev_close ??
      0
  );
  const pc = pickReasonablePrevClose(ltp, tickPc, row);
  const delta =
    Number.isFinite(ltp) && ltp > 0 && Number.isFinite(pc) ? ltp - pc : 0;
  const pct =
    Number.isFinite(pc) && pc !== 0 && Number.isFinite(ltp) && ltp > 0
      ? (delta / pc) * 100
      : 0;
  return { delta, pct };
}

/** Same change math as socket chgFmt — use on API load after tick-cache merge. */
export function applyIndexRowSocketChange(row, ltp, prevCloseFromTick = 0) {
  if (!row || typeof row !== "object") return row;
  const LTP = Number(ltp);
  if (!Number.isFinite(LTP) || LTP <= 0) return row;
  const fromRow = Number(row.__stablePrevClose ?? 0);
  const pc =
    Number.isFinite(fromRow) && fromRow > 0
      ? fromRow
      : (() => {
          const picked = pickReasonablePrevClose(LTP, prevCloseFromTick, row);
          return Number.isFinite(picked) && picked > 0 ? picked : 0;
        })();
  const chg = LTP - pc;
  const prevLTP = Number(row.lastTradedPrice ?? row.ltp ?? row.LTP ?? 0);
  return {
    ...row,
    lastTradedPrice: LTP,
    close: pc,
    __stablePrevClose: pc,
    colorLTP:
      prevLTP > LTP ? "#BF2114" : prevLTP < LTP ? "#009846" : row.colorLTP || "",
    changeAmount: chg.toFixed(2),
    changeAmountColor: chg >= 0 ? "text-success" : "text-danger",
    changePercent: LTP && pc && pc !== 0 ? (((LTP - pc) / pc) * 100).toFixed(2) : "0.00",
  };
}

/** After API + tick-cache merge, derive change % like the first socket tick. */
export function bootstrapIndexRowFromApi(row) {
  const ltp = indexRowLtp(row);
  if (!ltp) return row;
  return applyIndexRowSocketChange(row, ltp, 0);
}

/**
 * Same symbol family for live ticks: GOLD matches "GOLD 30Dec2026" / GOLD30DEC2026.
 * Does not treat GOLDMINI as GOLD (raw startsWith stole those quotes).
 */
export function namesShareScriptFamily(tickName, rowName) {
  const a = String(tickName || "").trim().toUpperCase();
  const b = String(rowName || "").trim().toUpperCase();
  if (!a || !b) return false;
  if (a === b) return true;

  const compactA = a.replace(/\s+/g, "");
  const compactB = b.replace(/\s+/g, "");
  if (compactA === compactB) return true;

  const tokenA = a.split(/\s/)[0] || "";
  const tokenB = b.split(/\s/)[0] || "";
  if (tokenA && tokenB && tokenA === tokenB) return true;

  const prefixThenNonLetter = (full, prefix) => {
    if (!prefix || prefix.length < 2 || !full.startsWith(prefix) || full === prefix) return false;
    const next = full.charAt(prefix.length);
    return !/[A-Z]/.test(next);
  };

  return prefixThenNonLetter(compactA, tokenB) || prefixThenNonLetter(compactB, tokenA);
}
